import React, { useEffect, useMemo, useRef, useState } from 'react';
import './WaterTankWidget.css';
import {
  buildWavePath,
  clamp,
  colorWithAlpha,
  formatDisplayValue,
  mixColors,
  parseColorToRgb,
  pickReadableColor,
  resolveLiquidColor,
} from './liquidShared';

const LEGACY_CARD_BACKGROUND =
  'linear-gradient(180deg, rgba(7, 18, 48, 0.98) 0%, rgba(7, 18, 46, 0.95) 100%)';
const WATER_TANK_CLIP_PATH =
  'M108 102 C132 86 288 86 312 102 L312 236 C288 250 132 250 108 236 Z';
const WATER_TANK_TOP_RIM_PATH =
  'M108 102 C132 118 288 118 312 102';
const WATER_TANK_LOWER_RIM_PATH =
  'M108 236 C132 220 288 220 312 236';
const WAVE_BASELINE = 142;
const WATER_SURFACE_EMPTY_Y = 228;
const WATER_SURFACE_FULL_Y = 110;
const THEME_PANEL_COLOR = 'var(--header-background)';
const THEME_SURFACE_COLOR = 'var(--cell-background)';
const THEME_HIGHLIGHT_COLOR = 'var(--part-color)';
const THEME_TEXT_COLOR = 'var(--widget-title-color)';
const THEME_HEADER_COLOR = 'var(--header-color)';
const THEME_SHADOW_COLOR = 'var(--shadow-black-soft)';
const LEGACY_WATER_TANK_TITLE_COLORS = ['#F9FBFF', '#FFFFFF', 'rgba(250, 252, 255, 0.96)', 'rgba(255, 255, 255, 0.96)', 'rgba(255, 255, 255, 1)'];
const LEGACY_WATER_TANK_VALUE_COLORS = ['#F3F9FF', '#FFFFFF', 'rgba(243, 249, 255, 1)', 'rgba(255, 255, 255, 1)'];
const LEGACY_WATER_TANK_UNIT_COLORS = ['rgba(222, 234, 255, 0.76)', '#FFFFFF', 'rgba(255, 255, 255, 0.84)'];

const colorsEqual = (left, right) => {
  const rgbLeft = parseColorToRgb(left);
  const rgbRight = parseColorToRgb(right);

  if (!rgbLeft || !rgbRight) {
    return String(left || '').trim() === String(right || '').trim();
  }

  return rgbLeft.every((channel, index) => channel === rgbRight[index]);
};

const matchesAnyColor = (value, candidates) =>
  candidates.some((candidate) => colorsEqual(value, candidate));

const WaterTankWidget = ({
  id,
  title,
  transparentBackground,
  min,
  max,
  value,
  unit,
  showUnit = true,
  color,
  colors,
  changeColorBasedOnValue = false,
  colorGrading = false,
  cardBackgroundColor,
  titleColor,
  valueColor,
  unitColor,
  glowColor,
  draggable,
  dims,
  decimals,
}) => {
  const [cursorState, setCursorState] = useState('idle');
  const [animatedValue, setAnimatedValue] = useState(0);
  const animationFrameRef = useRef(null);
  const previousValueRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const minNum = useMemo(() => {
    const numeric = Number(min);
    return Number.isFinite(numeric) ? numeric : 0;
  }, [min]);

  const maxNum = useMemo(() => {
    const numeric = Number(max);
    if (Number.isFinite(numeric)) {
      return numeric >= minNum ? numeric : minNum;
    }
    return Math.max(minNum + 100, 100);
  }, [max, minNum]);

  const targetValue = useMemo(() => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return minNum;
    return clamp(numeric, minNum, maxNum);
  }, [maxNum, minNum, value]);

  useEffect(() => {
    if (prefersReducedMotion) {
      setAnimatedValue(targetValue);
      previousValueRef.current = targetValue;
      return undefined;
    }

    const startValue = Number.isFinite(previousValueRef.current) ? previousValueRef.current : minNum;
    const delta = targetValue - startValue;
    const duration = Math.min(1400, Math.max(550, Math.abs(delta) * 14));
    const startedAt = performance.now();

    const tick = (timestamp) => {
      const elapsed = timestamp - startedAt;
      const progress = clamp(elapsed / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + delta * eased;

      setAnimatedValue(nextValue);

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(tick);
      } else {
        previousValueRef.current = targetValue;
      }
    };

    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [minNum, prefersReducedMotion, targetValue]);

  const normalizedValue = useMemo(() => {
    const range = maxNum - minNum;
    if (!(range > 0)) return 0;
    return clamp((animatedValue - minNum) / range, 0, 1);
  }, [animatedValue, maxNum, minNum]);

  const activeWaterColor = useMemo(
    () =>
      resolveLiquidColor({
        color,
        value: animatedValue,
        min: minNum,
        max: maxNum,
        changeColorBasedOnValue,
        colorGrading,
        colors,
      }),
    [animatedValue, changeColorBasedOnValue, color, colorGrading, colors, maxNum, minNum]
  );

  const svgIdBase = useMemo(
    () => String(id || 'water-tank-widget').replace(/[^A-Za-z0-9_-]/g, '_'),
    [id]
  );
  const clipId = `${svgIdBase}-clip`;
  const frontGradientId = `${svgIdBase}-front-gradient`;
  const backGradientId = `${svgIdBase}-back-gradient`;
  const panelGlowId = `${svgIdBase}-panel-glow`;
  const shellGradientId = `${svgIdBase}-shell-gradient`;
  const metalGradientId = `${svgIdBase}-metal-gradient`;

  const surfaceY = useMemo(() => {
    const liftedProgress = Math.pow(normalizedValue, 1.32);
    return clamp(
      WATER_SURFACE_EMPTY_Y -
        liftedProgress * (WATER_SURFACE_EMPTY_Y - WATER_SURFACE_FULL_Y),
      WATER_SURFACE_FULL_Y,
      WATER_SURFACE_EMPTY_Y
    );
  }, [normalizedValue]);

  const waveAmplitude = useMemo(() => 8 + normalizedValue * 7, [normalizedValue]);
  const frontWavePath = useMemo(
    () =>
      buildWavePath({
        amplitude: waveAmplitude,
        baseline: WAVE_BASELINE,
        width: 290,
        segmentWidth: 44,
        height: 268,
        invert: false,
      }),
    [waveAmplitude]
  );
  const backWavePath = useMemo(
    () =>
      buildWavePath({
        amplitude: waveAmplitude * 0.8,
        baseline: WAVE_BASELINE,
        width: 290,
        segmentWidth: 50,
        height: 268,
        invert: true,
      }),
    [waveAmplitude]
  );

  const waterBrightColor = useMemo(
    () => mixColors(activeWaterColor, THEME_HIGHLIGHT_COLOR, 0.2),
    [activeWaterColor]
  );
  const waterMidColor = useMemo(
    () => mixColors(activeWaterColor, THEME_PANEL_COLOR, 0.24),
    [activeWaterColor]
  );
  const waterDeepColor = useMemo(
    () => mixColors(activeWaterColor, THEME_SHADOW_COLOR, 0.36),
    [activeWaterColor]
  );
  const waterShadowColor = useMemo(
    () => mixColors(activeWaterColor, THEME_SHADOW_COLOR, 0.54),
    [activeWaterColor]
  );
  const resolvedGlowColor = useMemo(
    () => String(glowColor || '').trim() || colorWithAlpha(activeWaterColor, 0.48),
    [activeWaterColor, glowColor]
  );
  const waterContrastColor = useMemo(
    () => pickReadableColor(activeWaterColor, THEME_HIGHLIGHT_COLOR, THEME_TEXT_COLOR),
    [activeWaterColor]
  );
  const panelTone = useMemo(
    () => mixColors(THEME_PANEL_COLOR, activeWaterColor, 0.18),
    [activeWaterColor]
  );
  const tankShellLight = useMemo(
    () => mixColors(panelTone, THEME_HIGHLIGHT_COLOR, 0.16),
    [panelTone]
  );
  const tankShellMid = useMemo(
    () => mixColors(panelTone, THEME_HEADER_COLOR, 0.14),
    [panelTone]
  );
  const tankShellDark = useMemo(
    () => mixColors(panelTone, THEME_SHADOW_COLOR, 0.24),
    [panelTone]
  );
  const tankMetalBase = useMemo(
    () => mixColors(THEME_HIGHLIGHT_COLOR, THEME_HEADER_COLOR, 0.42),
    []
  );
  const tankMetalLight = useMemo(
    () => mixColors(tankMetalBase, THEME_SURFACE_COLOR, 0.28),
    [tankMetalBase]
  );
  const tankMetalDark = useMemo(
    () => mixColors(tankMetalBase, THEME_SHADOW_COLOR, 0.16),
    [tankMetalBase]
  );
  const tankMetalOutline = useMemo(
    () => mixColors(tankMetalDark, THEME_TEXT_COLOR, 0.18),
    [tankMetalDark]
  );
  const tankSupportPrimary = useMemo(
    () => mixColors(tankMetalDark, THEME_SHADOW_COLOR, 0.1),
    [tankMetalDark]
  );
  const tankSupportSecondary = useMemo(
    () => mixColors(tankMetalLight, THEME_HEADER_COLOR, 0.24),
    [tankMetalLight]
  );
  const tankTopDeckColor = useMemo(
    () => mixColors(tankShellMid, THEME_SHADOW_COLOR, 0.18),
    [tankShellMid]
  );
  const tankOutlineColor = useMemo(
    () => colorWithAlpha(mixColors(tankMetalLight, THEME_TEXT_COLOR, 0.24), 0.7),
    [tankMetalLight]
  );
  const tankRibUpperColor = useMemo(
    () => colorWithAlpha(mixColors(tankMetalLight, THEME_HIGHLIGHT_COLOR, 0.08), 0.42),
    [tankMetalLight]
  );
  const tankRibLowerColor = useMemo(
    () => colorWithAlpha(mixColors(tankMetalDark, THEME_TEXT_COLOR, 0.08), 0.28),
    [tankMetalDark]
  );
  const tankInteriorShadePrimary = useMemo(
    () => colorWithAlpha(mixColors(tankShellDark, THEME_SHADOW_COLOR, 0.22), 0.26),
    [tankShellDark]
  );
  const tankInteriorShadeSecondary = useMemo(
    () => colorWithAlpha(mixColors(tankShellMid, THEME_SHADOW_COLOR, 0.22), 0.2),
    [tankShellMid]
  );
  const tankSurfaceStrokeColor = useMemo(
    () => colorWithAlpha(mixColors(waterBrightColor, THEME_HIGHLIGHT_COLOR, 0.18), 0.28),
    [waterBrightColor]
  );
  const tankSurfaceHighlightColor = useMemo(
    () => colorWithAlpha(THEME_HIGHLIGHT_COLOR, 0.1),
    []
  );
  const resolvedTitleColor = useMemo(() => {
    const normalized = String(titleColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_WATER_TANK_TITLE_COLORS)) {
      return THEME_TEXT_COLOR;
    }

    return normalized;
  }, [titleColor]);
  const resolvedValueColor = useMemo(() => {
    const normalized = String(valueColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_WATER_TANK_VALUE_COLORS)) {
      return waterContrastColor;
    }

    return normalized;
  }, [valueColor, waterContrastColor]);
  const resolvedUnitColor = useMemo(() => {
    const normalized = String(unitColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_WATER_TANK_UNIT_COLORS)) {
      return colorWithAlpha(waterContrastColor, 0.84);
    }

    return normalized;
  }, [unitColor, waterContrastColor]);

  const width = Number.isFinite(Number(dims?.width)) ? Number(dims.width) : 248;
  const height = Number.isFinite(Number(dims?.height)) ? Number(dims.height) : 248;
  const valueScale = Math.max(0.9, Math.min(1.18, Math.min(width / 248, height / 248)));
  const formattedValue = useMemo(
    () => formatDisplayValue(animatedValue, decimals),
    [animatedValue, decimals]
  );
  const unitText = useMemo(() => {
    if (!showUnit) return '';
    const normalizedUnit = String(unit || '').trim();
    return normalizedUnit;
  }, [showUnit, unit]);
  const valueSeparator = unitText === '%' ? '' : ' ';
  const displayTitle = useMemo(() => {
    const normalized = title == null ? '' : String(title);
    return normalized.trim() ? normalized : '';
  }, [title]);

  const resolvedCardBackground = useMemo(() => {
    const normalized = String(cardBackgroundColor || '').trim();
    if (!normalized || normalized === LEGACY_CARD_BACKGROUND) {
      return 'var(--header-background)';
    }
    return normalized;
  }, [cardBackgroundColor]);

  const containerStyle = useMemo(
    () => ({
      '--water-tank-card-bg': resolvedCardBackground,
      '--water-tank-title-color': resolvedTitleColor,
      '--water-tank-value-color': resolvedValueColor,
      '--water-tank-unit-color': resolvedUnitColor,
      '--water-tank-glow-color': resolvedGlowColor,
      '--water-tank-value-scale': valueScale,
      '--water-tank-bubble-color': colorWithAlpha(THEME_HIGHLIGHT_COLOR, 0.2),
      '--water-tank-title-shadow': colorWithAlpha(THEME_SHADOW_COLOR, 0.22),
      '--water-tank-value-shadow-primary': colorWithAlpha(THEME_SHADOW_COLOR, 0.24),
      '--water-tank-value-shadow-secondary': colorWithAlpha(THEME_SHADOW_COLOR, 0.18),
    }),
    [
      resolvedCardBackground,
      resolvedTitleColor,
      resolvedUnitColor,
      resolvedValueColor,
      resolvedGlowColor,
      valueScale,
    ]
  );

  const cursorClass = draggable
    ? cursorState === 'grabbing'
      ? 'water-tank-widget--grabbing'
      : 'water-tank-widget--grab'
    : 'water-tank-widget--auto';

  return (
    <div
      className={[
        'widget-container',
        'water-tank-widget',
        cursorClass,
        transparentBackground ? 'water-tank-widget--transparent' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={containerStyle}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(event) => {
        const widgetId = event.target.closest('.widget')?.id;
        if (!widgetId) return;
        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;
        let dragData = `water-tank;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        event.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {displayTitle ? (
        <div className="water-tank-widget__header">
          <h3 className="water-tank-widget__title">{displayTitle}</h3>
        </div>
      ) : null}

      <div className="water-tank-widget__body">
        <div className="water-tank-widget__scene">
          <svg
            className="water-tank-widget__svg"
            viewBox="84 24 252 266"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <clipPath id={clipId}>
                <path d={WATER_TANK_CLIP_PATH} />
              </clipPath>
              <linearGradient id={frontGradientId} x1="124" y1="88" x2="308" y2="246" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterBrightColor, 0.96)} />
                <stop offset="60%" stopColor={colorWithAlpha(waterMidColor, 0.92)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterDeepColor, 0.98)} />
              </linearGradient>
              <linearGradient id={backGradientId} x1="120" y1="96" x2="300" y2="234" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={colorWithAlpha(waterBrightColor, 0.42)} />
                <stop offset="100%" stopColor={colorWithAlpha(waterShadowColor, 0.6)} />
              </linearGradient>
              <radialGradient id={panelGlowId} cx="50%" cy="58%" r="42%">
                <stop offset="0%" stopColor={colorWithAlpha(resolvedGlowColor, 0.64)} />
                <stop offset="100%" stopColor={colorWithAlpha(resolvedGlowColor, 0)} />
              </radialGradient>
              <linearGradient id={shellGradientId} x1="102" y1="84" x2="318" y2="248" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={tankShellLight} />
                <stop offset="52%" stopColor={tankShellMid} />
                <stop offset="100%" stopColor={tankShellDark} />
              </linearGradient>
              <linearGradient id={metalGradientId} x1="104" y1="38" x2="312" y2="110" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={tankMetalLight} />
                <stop offset="52%" stopColor={tankMetalBase} />
                <stop offset="100%" stopColor={tankMetalDark} />
              </linearGradient>
            </defs>

            <ellipse
              className="water-tank-widget__panelGlow"
              cx="210"
              cy="170"
              rx={94 + normalizedValue * 30}
              ry={52 + normalizedValue * 18}
              fill={`url(#${panelGlowId})`}
            />

            <g className="water-tank-widget__support">
              <path d="M176 236 L190 236 L182 262 Q176 266 169 262 Z" fill={tankSupportPrimary} />
              <path d="M230 236 L244 236 L251 262 Q244 266 238 262 Z" fill={tankSupportPrimary} />
              <path d="M136 234 L154 236 L132 286 L118 282 Z" fill={tankSupportPrimary} />
              <path d="M156 234 L168 236 L136 276 L126 270 Z" fill={tankSupportSecondary} />
              <path d="M264 234 L276 236 L308 276 L298 270 Z" fill={tankSupportSecondary} />
              <path d="M284 234 L302 236 L320 282 L306 286 Z" fill={tankSupportPrimary} />
            </g>

            <g className="water-tank-widget__tank">
              <path d={WATER_TANK_CLIP_PATH} fill={`url(#${shellGradientId})`} />

              <g clipPath={`url(#${clipId})`}>
                <rect x="104" y="92" width="212" height="148" fill={colorWithAlpha(activeWaterColor, 0.18)} />
                <ellipse
                  className="water-tank-widget__backGlow"
                  cx="210"
                  cy={194 - normalizedValue * 34}
                  rx={82 + normalizedValue * 28}
                  ry={28 + normalizedValue * 12}
                  fill={colorWithAlpha(resolvedGlowColor, 0.3)}
                />

                <g transform={`translate(0 ${surfaceY - WAVE_BASELINE})`}>
                  <g className="water-tank-widget__wave water-tank-widget__wave--back">
                    <path d={backWavePath} fill={`url(#${backGradientId})`} />
                  </g>
                  <g className="water-tank-widget__wave water-tank-widget__wave--front">
                    <path d={frontWavePath} fill={`url(#${frontGradientId})`} />
                    <path
                      d={frontWavePath}
                      fill="none"
                      stroke={tankSurfaceStrokeColor}
                      strokeWidth="1.5"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                </g>

                <ellipse
                  className="water-tank-widget__surfaceHighlight"
                  cx={168 + normalizedValue * 24}
                  cy={surfaceY + 10}
                  rx="52"
                  ry="20"
                  fill={tankSurfaceHighlightColor}
                  transform={`rotate(-14 ${168 + normalizedValue * 24} ${surfaceY + 10})`}
                />

                {normalizedValue > 0.08 ? (
                  <g className="water-tank-widget__bubbleLayer">
                    <circle className="water-tank-widget__bubble water-tank-widget__bubble--one" cx="170" cy="192" r="7" />
                    <circle className="water-tank-widget__bubble water-tank-widget__bubble--two" cx="208" cy="204" r="5.4" />
                    <circle className="water-tank-widget__bubble water-tank-widget__bubble--three" cx="250" cy="182" r="5.8" />
                    <circle className="water-tank-widget__bubble water-tank-widget__bubble--four" cx="278" cy="154" r="4.2" />
                  </g>
                ) : null}
              </g>

              <path
                d={WATER_TANK_CLIP_PATH}
                fill="none"
                stroke={tankOutlineColor}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={WATER_TANK_TOP_RIM_PATH}
                fill="none"
                stroke={tankRibUpperColor}
                strokeWidth="2.2"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={WATER_TANK_LOWER_RIM_PATH}
                fill="none"
                stroke={tankRibLowerColor}
                strokeWidth="1.6"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M108 140 C132 154 288 154 312 140"
                fill="none"
                stroke={tankRibUpperColor}
                strokeWidth="1.8"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M108 174 C132 188 288 188 312 174"
                fill="none"
                stroke={tankRibUpperColor}
                strokeWidth="1.8"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M108 208 C132 222 288 222 312 208"
                fill="none"
                stroke={tankRibLowerColor}
                strokeWidth="1.8"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M104 126 C128 138 292 138 316 126 L316 156 C292 168 128 168 104 156 Z"
                fill={tankInteriorShadePrimary}
              />
              <path
                d="M104 160 C128 172 292 172 316 160 L316 192 C292 204 128 204 104 192 Z"
                fill={tankInteriorShadeSecondary}
              />
              <path
                d="M104 194 C128 206 292 206 316 194 L316 226 C292 238 128 238 104 226 Z"
                fill={tankInteriorShadePrimary}
              />
            </g>

            <g className="water-tank-widget__topAssembly">
              <rect x="108" y="58" width="204" height="32" rx="3" fill={`url(#${metalGradientId})`} />
              <ellipse cx="210" cy="90" rx="104" ry="23" fill={tankTopDeckColor} />
              <ellipse
                cx="210"
                cy="90"
                rx="104"
                ry="23"
                fill="none"
                stroke={tankOutlineColor}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              <rect x="186" y="42" width="48" height="48" fill={`url(#${metalGradientId})`} />
              <ellipse cx="210" cy="42" rx="50" ry="8" fill={tankMetalLight} />
              <ellipse
                cx="210"
                cy="42"
                rx="50"
                ry="8"
                fill="none"
                stroke={colorWithAlpha(tankMetalOutline, 0.92)}
                strokeWidth="1.6"
                vectorEffect="non-scaling-stroke"
              />
              <ellipse cx="210" cy="90" rx="24" ry="4" fill={colorWithAlpha(tankMetalDark, 0.46)} />
            </g>
          </svg>

          <div className="water-tank-widget__value">
            <span className="water-tank-widget__valueNumber">{formattedValue}</span>
            {unitText ? (
              <span className="water-tank-widget__valueUnit">
                {valueSeparator}
                {unitText}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterTankWidget;
